import { useEffect, useRef } from "react";
import { Admin, Authenticated, CustomRoutes, Resource, defaultTheme } from "react-admin";

import { ThemeProvider, createTheme, StyledEngineProvider } from "@mui/material";
import { AlertRuleList, AlertRuleCreate, AlertRuleEdit } from "./components/admin/AlertRuleAdmin";

import UserIcon from "@mui/icons-material/Group";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";

import { config } from "./config";
import lvApiProvider from "./providers/lvApiProvider";
import authProvider from "./providers/authProvider";

import { SitesList, SitesCreate, SitesEdit, SitesShow } from "./components/admin/SiteAdmin";
import { SiteGroupCreate, SiteGroupEdit, SiteGroupList } from "./components/admin/SiteGroupAdmin";
import { RoleGroupList, RoleGroupCreate, RoleGroupEdit } from "./components/admin/RoleGroupAdmin";
import { RoleList, RoleCreate, RoleEdit } from "./components/admin/RoleAdmin";
import { UserList, UserEdit, UserCreate } from "./components/admin/UserAdmin";
import { ChartSubchartList, ChartSubchartCreate, ChartSubchartEdit } from "./components/admin/ChartSubchartAdmin";
import { ClientChartList, ClientChartCreate, ClientChartEdit } from "./components/admin/ClientChartAdmin";
import { ChartSeriesList, ChartSeriesCreate, ChartSeriesEdit } from "./components/admin/ChartSeriesAdmin";
import { ClientMfaList, ClientMfaCreate, ClientMfaEdit } from "./components/admin/ClientMfaAdmin";
import { AuditTrailList } from "./components/admin/AuditTrailAdmin";
import { SiteGroupCheatSheetList } from "./components/admin/SiteGroupCheatSheetAdmin";
import { ClientList, ClientShow } from "./components/admin/ClientAdmin";
import { OnPremFileList, OnPremFileCreate, OnPremFileEdit } from "./components/admin/OnPremFileAdmin";

import {
    EmergencyRatingConfigCreate,
    EmergencyRatingConfigEdit,
    EmergencyRatingConfigList,
} from "./components/admin/EmergencyRatingConfigAdmin";

import { EdgeConfigV3List } from "./components/admin/telemetry/EdgeConfig/index";

import SignIn from "./components/core/SignIn";
import CustomLayout from "./CustomLayout";

import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import EditV3EdgeConfig from "./components/custom/edgeConfig/EditV3EdgeConfig";
import CreateV3EdgeConfig from "./components/custom/edgeConfig/CreateV3EdgeConfig";
import { LineRoleGroupEdit, LineRoleGroupList } from "./components/admin/LineRoleGroupAdmin";
import { BrowserRouter, Route } from "react-router-dom";
import Dashboard from "./components/core/Dashboard";
import DeviceComms from "./components/custom/DeviceComms";
import DeviceHealthMain from "./components/custom/deviceHealth/DeviceHealthMain";
import UserAnalytics from "./components/custom/UserAnalytics";
import UserAnalyticsShow from "./components/custom/UserAnalyticsShow";
import LidarView from "./components/custom/deviceHealth/V3/LidarView";
import V3View from "./components/custom/deviceHealth/V3/V3View";
import V3List from "./components/custom/deviceHealth/V3/V3List";
import EdgeDeviceHealth from "./components/custom/deviceHealth/EdgeDeviceHealth";
import IFrameApp from "./components/core/IFrameApp";
import MDUploadApp from "./components/custom/calibrationPlatform/mdUpload/MDUploadTool";
import CADUploadTool from "./components/custom/calibrationPlatform/CADUploadTool";
import PlscaddKmzIngressTool from "./components/custom/calibrationPlatform/plscaddKmzIngress/PlscaddKmzIngressTool";
import PITUploadTool from "./components/custom/calibrationPlatform/PITUploadTool";
import PITPipeline from "./components/custom/calibrationPlatform/pitPipeline/PITPipeline";
import PalmCatenaryViewer from "./components/custom/calibrationPlatform/PalmCatenaryViewer";
import PalmCoefficients from "./components/custom/calibrationPlatform/PalmCoefficients";
import Maple from "./components/custom/calibrationPlatform/Maple";
import ProgressViewer from "./components/custom/calibrationPlatform/ProgressViewer";
import DeviceClientAssetDataLinkingApp from "./components/custom/calibrationPlatform/deviceLinking/DeviceClientAssetDataLinkingApp";
import LatestDatasetSelector from "./components/custom/calibrationPlatform/LatestDatasetSelector";
import ReferenceRatingUpload from "./components/custom/calibrationPlatform/ReferenceRatingUpload";
import Aspen from "./components/custom/calibrationPlatform/aspen/Aspen";
import SensorCounts from "./components/custom/calibrationPlatform/sensorCounts/SensorCounts";
import FirmwareProvisionPlatform from "./components/custom/ProvisionPlatfrom";
import { EmergencyRatingConfigSection } from "./components/custom/emergencyRatings/EmergencyRatingConfigSection";
import { GlobalToastContainer } from "./utils/GlobalToast";
import TenxViewport from "./components/custom/10x/viewport/10xViewport";

export const theme = createTheme({
    ...defaultTheme,
    palette: {
        primary: {
            main: "#1976d2",
        },
        secondary: {
            main: "#0288d1",
        },
        background: {
            default: "#fafafa",
        },
    },
    sidebar: {
        width: 240,
        closedWidth: 0,
    },
});

const App = () => {
    const sagTempIFrameRef = useRef();

    // Receive URL updates from sag/temp
    useEffect(() => {
        const handleMessage = (event) => {
            if (event.origin !== new URL(config.sagTempUiUrl).origin) {
                console.warn("Ignored message from origin:", event.origin);
                return;
            }

            const { type, state } = event.data;
            if (type === "UPDATE_URL") {
                const { selectedSiteGroupId, dateRange, filterData } = state;
                const query = new URLSearchParams({
                    selectedSiteGroupId,
                    startDate: dateRange[0],
                    endDate: dateRange[1],
                    filterData: encodeURIComponent(JSON.stringify(filterData)),
                }).toString();
                window.history.pushState({}, "", `?${query}`);
            }
        };

        window.addEventListener("message", handleMessage);

        return () => {
            window.removeEventListener("message", handleMessage);
        };
    }, []);

    // Send initial load message to sag/temp to populate the URL
    const sendInitialLoadMessage = () => {
        const iframe = sagTempIFrameRef.current;
        const iframeOrigin = new URL(config.sagTempUiUrl).origin;

        if (!iframe || !iframeOrigin) return;

        const decodedURL = decodeURIComponent(new URLSearchParams(window.location.search).toString());
        const initialLoadMessage = {
            type: "INITIAL_LOAD",
            state: decodedURL,
        };

        iframe.contentWindow.postMessage(initialLoadMessage, iframeOrigin);
    };

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <BrowserRouter>
                        <Admin
                            layout={CustomLayout}
                            theme={theme}
                            loginPage={SignIn}
                            dashboard={Dashboard}
                            authProvider={authProvider}
                            dataProvider={lvApiProvider}
                        >
                            <Resource
                                name="portal/sites"
                                list={SitesList}
                                edit={SitesEdit}
                                create={SitesCreate}
                                show={SitesShow}
                            />
                            <Resource
                                name="portal/siteGroups"
                                list={SiteGroupList}
                                edit={SiteGroupEdit}
                                create={SiteGroupCreate}
                            />
                            <Resource name="portal/lineRoleGroups" list={LineRoleGroupList} edit={LineRoleGroupEdit} />
                            <Resource
                                name="portal/users"
                                list={UserList}
                                edit={UserEdit}
                                create={UserCreate}
                                icon={UserIcon}
                            />
                            <Resource name="portal/roles" list={RoleList} edit={RoleEdit} create={RoleCreate} />
                            <Resource
                                name="portal/roleGroups"
                                list={RoleGroupList}
                                edit={RoleGroupEdit}
                                create={RoleGroupCreate}
                            />
                            <Resource
                                name="portal/clientCharts"
                                list={ClientChartList}
                                edit={ClientChartEdit}
                                create={ClientChartCreate}
                            />
                            <Resource
                                name="portal/chartSeries"
                                list={ChartSeriesList}
                                edit={ChartSeriesEdit}
                                create={ChartSeriesCreate}
                            />
                            <Resource
                                name="portal/chartSubcharts"
                                list={ChartSubchartList}
                                edit={ChartSubchartEdit}
                                create={ChartSubchartCreate}
                            />
                            <Resource
                                name="portal/alertRules"
                                options={{ label: "Alert Rules" }}
                                list={AlertRuleList}
                                create={AlertRuleCreate}
                                edit={AlertRuleEdit}
                                icon={NotificationsActiveIcon}
                            />
                            <Resource name="portal/clients" list={ClientList} show={ClientShow} />
                            <Resource
                                name="portal/onPremFiles"
                                list={OnPremFileList}
                                create={OnPremFileCreate}
                                edit={OnPremFileEdit}
                                options={{ label: "On-Prem Files" }}
                            />
                            <Resource
                                name="portal/clientMfa"
                                list={ClientMfaList}
                                edit={ClientMfaEdit}
                                create={ClientMfaCreate}
                            />
                            <Resource name="portal/auditTrail" list={AuditTrailList} />
                            <Resource
                                name="portal/emergency-rating-config"
                                list={EmergencyRatingConfigList}
                                edit={EmergencyRatingConfigEdit}
                                create={EmergencyRatingConfigCreate}
                            />
                            <Resource
                                name="telemetry/edgeConfig/v3"
                                list={EdgeConfigV3List}
                                edit={EditV3EdgeConfig}
                                create={CreateV3EdgeConfig}
                            />
                            <Resource name="portal/sitesSummary" list={SiteGroupCheatSheetList} />
                            <Resource name="portal/sitesByClientId" />
                            <CustomRoutes>
                                <Route
                                    path="/portal/app/:appId/*"
                                    Component={() => (
                                        <Authenticated>
                                            <IFrameApp />
                                        </Authenticated>
                                    )}
                                />
                                <Route
                                    exact
                                    path="/portal/sag-temp"
                                    Component={() => (
                                        <Authenticated>
                                            <div style={{ width: "100%", height: "100%" }}>
                                                <iframe
                                                    ref={sagTempIFrameRef}
                                                    src={config.sagTempUiUrl}
                                                    style={{
                                                        border: "0",
                                                        width: "100%",
                                                        height: "100%",
                                                        overflow: "hidden",
                                                    }}
                                                    title="sag-temp"
                                                    onLoad={sendInitialLoadMessage}
                                                />
                                            </div>
                                        </Authenticated>
                                    )}
                                />
                                <Route
                                    exact
                                    path="/portal/emergency-rating-config-section"
                                    Component={() => (
                                        <Authenticated>
                                            <EmergencyRatingConfigSection />
                                        </Authenticated>
                                    )}
                                ></Route>
                                <Route
                                    exact
                                    path="/portal/confidence-interval-configuration"
                                    Component={() => (
                                        <Authenticated>
                                            <div style={{ width: "100%", height: "100%" }}>
                                                <iframe
                                                    src={config.appsmithPages.confidenceIntervalConfiguration}
                                                    seamless
                                                    style={{
                                                        border: "0",
                                                        width: "100%",
                                                        height: "100%",
                                                        overflow: "hidden",
                                                    }}
                                                    title="sag-temp"
                                                />
                                            </div>
                                        </Authenticated>
                                    )}
                                />
                                <Route
                                    exact
                                    path="/telemetry/device-health"
                                    Component={() => (
                                        <Authenticated>
                                            <DeviceHealthMain />
                                        </Authenticated>
                                    )}
                                />
                                <Route
                                    exact
                                    path="/telemetry/v3-device-health"
                                    Component={() => (
                                        <Authenticated>
                                            <EdgeDeviceHealth deviceType={"v3"} />
                                        </Authenticated>
                                    )}
                                />
                                <Route
                                    exact
                                    path="/telemetry/device-health/lidar-view/:id"
                                    Component={() => (
                                        <Authenticated>
                                            <LidarView />
                                        </Authenticated>
                                    )}
                                />
                                <Route
                                    exact
                                    path="/telemetry/device-health/v3/:id"
                                    Component={() => (
                                        <Authenticated>
                                            <V3View />
                                        </Authenticated>
                                    )}
                                />
                                <Route
                                    exact
                                    path="/telemetry/device-comms"
                                    Component={() => (
                                        <Authenticated>
                                            <DeviceComms />
                                        </Authenticated>
                                    )}
                                />
                                <Route
                                    exact
                                    path="/portal/user-analytics"
                                    Component={() => (
                                        <Authenticated>
                                            <UserAnalytics />
                                        </Authenticated>
                                    )}
                                />
                                <Route
                                    exact
                                    path="/portal/user-analytics/:id"
                                    Component={() => (
                                        <Authenticated>
                                            <UserAnalyticsShow />
                                        </Authenticated>
                                    )}
                                />

                                <Route
                                    exact
                                    path="/masterdata/assets/v3"
                                    Component={() => (
                                        <Authenticated>
                                            <V3List />
                                        </Authenticated>
                                    )}
                                />
                                <Route
                                    exact
                                    path="/calibration/masterdata"
                                    Component={() => (
                                        <Authenticated>
                                            <MDUploadApp />
                                        </Authenticated>
                                    )}
                                />
                                <Route
                                    exact
                                    path="/calibration/Datasets"
                                    Component={() => (
                                        <Authenticated>
                                            <LatestDatasetSelector />
                                        </Authenticated>
                                    )}
                                />
                                <Route
                                    exact
                                    path="/calibration/clientAsset"
                                    Component={() => (
                                        <Authenticated>
                                            <CADUploadTool />
                                        </Authenticated>
                                    )}
                                />
                                <Route
                                    exact
                                    path="/calibration/plscaddKmz"
                                    Component={() => (
                                        <Authenticated>
                                            <PlscaddKmzIngressTool />
                                        </Authenticated>
                                    )}
                                />
                                <Route
                                    exact
                                    path="/calibration/pitUpload"
                                    Component={() => (
                                        <Authenticated>
                                            <PITUploadTool />
                                        </Authenticated>
                                    )}
                                />
                                <Route
                                    exact
                                    path="/calibration/pitPipeline"
                                    Component={() => (
                                        <Authenticated>
                                            <PITPipeline />
                                        </Authenticated>
                                    )}
                                />
                                <Route
                                    exact
                                    path="/calibration/palmCatenaryViewer"
                                    Component={() => (
                                        <Authenticated>
                                            <PalmCatenaryViewer />
                                        </Authenticated>
                                    )}
                                />
                                <Route
                                    exact
                                    path="/calibration/palmCoefficients"
                                    Component={() => (
                                        <Authenticated>
                                            <PalmCoefficients />
                                        </Authenticated>
                                    )}
                                />
                                <Route
                                    exact
                                    path="/calibration/maple"
                                    Component={() => (
                                        <Authenticated>
                                            <Maple />
                                        </Authenticated>
                                    )}
                                />
                                <Route
                                    exact
                                    path="/calibration/progressViewer"
                                    Component={() => (
                                        <Authenticated>
                                            <ProgressViewer />
                                        </Authenticated>
                                    )}
                                />
                                <Route
                                    exact
                                    path="/calibration/deviceClientAssetLinking"
                                    Component={() => (
                                        <Authenticated>
                                            <DeviceClientAssetDataLinkingApp />
                                        </Authenticated>
                                    )}
                                />
                                <Route
                                    exact
                                    path="/calibration/referenceRatingUpload"
                                    Component={() => (
                                        <Authenticated>
                                            <ReferenceRatingUpload />
                                        </Authenticated>
                                    )}
                                />
                                <Route
                                    exact
                                    path="/calibration/sensorCounts"
                                    Component={() => (
                                        <Authenticated>
                                            <SensorCounts />
                                        </Authenticated>
                                    )}
                                />
                                <Route
                                    exact
                                    path="/calibration/aspen"
                                    Component={() => (
                                        <Authenticated>
                                            <Aspen />
                                        </Authenticated>
                                    )}
                                />
                                <Route
                                    exact
                                    path="/device/provision"
                                    Component={() => (
                                        <Authenticated>
                                            <FirmwareProvisionPlatform />
                                        </Authenticated>
                                    )}
                                />
                                <Route
                                    exact
                                    path="/10x/viewport"
                                    Component={() => (
                                        <Authenticated>
                                            <TenxViewport />
                                        </Authenticated>
                                    )}
                                />
                                <Route
                                    exact
                                    path="/"
                                    Component={() => (
                                        <Authenticated>
                                            <Dashboard />
                                        </Authenticated>
                                    )}
                                />
                            </CustomRoutes>
                        </Admin>
                    </BrowserRouter>
                </LocalizationProvider>
            </ThemeProvider>
            <sha-version id="sha-version" style={{ display: "none" }}>
                {process.env.REACT_APP_GIT_SHA || "unknown"}
            </sha-version>
            <GlobalToastContainer />
        </StyledEngineProvider>
    );
};

let username;

const detectUser = () => {
    try {
        let user = localStorage.getItem("user");

        if (user) {
            user = JSON.parse(user);
            let header = document.getElementsByTagName("header");

            if (header && header[0]) {
                let h6 = header[0].querySelector("h6");

                if (h6 && user?.username) {
                    username = user.username;
                    h6.insertAdjacentHTML("afterEnd", `<span>Welcome, ${user.username} </span>`);
                }
            }
        }
    } catch (_e) {
        console.warn("Unable to set username in header");
    }

    if (!username) {
        setTimeout(detectUser, 1000);
    }
};

(function () {
    setTimeout(detectUser, 100);
})();

export default App;
